// umlaut-check: skip — Variablen-/Funktionsnamen ("kwVon", "ladeListe" usw.)
// sind technische Bezeichner; sichtbare Texte tragen Umlaute korrekt.

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Container, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {router, usePage} from "@inertiajs/react";
import axios from "axios";
import {PiPlus, PiUploadSimple} from "react-icons/pi";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import SelectList from "../../Components/SelectList.jsx";
import KwSelect from "../../Components/Cosmos/KwSelect.jsx";
import DateiImportModal from "../../Components/Cosmos/DateiImportModal.jsx";
import ImporteListe from "../../Components/Cosmos/ImporteListe.jsx";
import PufferProduktionAbzeichen from "../../Components/Cosmos/PufferProduktionAbzeichen.jsx";
import {AMPEL_FARBEN, AMPEL_LABEL} from "../../Components/Cosmos/AmpelSelect.jsx";
import {kwLabel} from "../../Components/Cosmos/KwSelect.jsx";
import {formatMenge, markeLabel} from "../../Components/Cosmos/format.js";
import {compareForSort} from "../../functions/sorting.js";
import useGemerkteFilter, {AlleFilterZuruecksetzenLink} from "../../Components/Cosmos/useGemerkteFilter.jsx";
import TabellenUeberlaufSchatten, {useTabellenUeberlauf} from "../../Components/Cosmos/TabellenUeberlaufSchatten.jsx";

// T12 (Rückmeldung 22.09.2026): Filter UND Sortierung bleiben beim
// Verlassen und Zurückkommen erhalten (useGemerkteFilter, Paket S5) - die
// Sortierung ("sortKey"/"sortDir") ist bewusst Teil desselben gemerkten
// Zustands wie die Filter, kein zweiter Speicher.
const STANDARD_FILTER = {kwVon: null, kwBis: null, status: "", marke: "", zielort: "", q: "", sortKey: null, sortDir: "asc"};

// T14 (Rückmeldung 22.09.2026): jede Spalte klickbar sortierbar
// (SortableTh-Muster wie Stock/B2bRunDetail.jsx) - "getValue" liefert den
// Vergleichswert, "type" steuert text- vs. zahlenweisen Vergleich
// (functions/sorting.js). Ohne Klick bleibt es bei der natürlichen
// Server-Reihenfolge (LieferungenQuery::liste(): ab der aktuellen Woche
// aufsteigend, Vergangenheit danach absteigend).
const AMPEL_SCHWERE_REIHENFOLGE = {rot: 5, gelb: 4, rosa: 3, gruen: 2, weiss: 1};

const SPALTEN = [
    {key: "kw", id: "kw", label: "KW", help: "Kalenderwoche, der diese Lieferung zugeordnet ist.", type: "text", getValue: (l) => l.kw},
    {key: "lkw_bezeichnung", id: "lkw", label: "LKW", help: 'Freitext-Bezeichnung des LKW, z. B. "Truck 1 CW39".', type: "text", getValue: (l) => l.lkw_bezeichnung},
    {key: "cosmos_order_id", id: "cosmos-nr", label: "Cosmos-Nr.", help: "Cosmos-Auftragsnummer dieser Lieferung, sobald bekannt.", type: "text", getValue: (l) => l.cosmos_order_id},
    {key: "weclapp_bestellnummer", id: "weclapp", label: "Weclapp", help: 'Nur ein Verweis auf die Weclapp-Bestellung (z. B. "P1090") - keine Datenquelle.', type: "text", getValue: (l) => l.weclapp_bestellnummer},
    {key: "status", id: "status", label: "Status", help: "Aktueller Status der gesamten Lieferung.", type: "text", getValue: (l) => STATUS_LABEL[l.status] || l.status},
    {key: "marke", id: "marke", label: "Marke", help: "Molotow, Burner, Flame oder Cosmos.", type: "text", getValue: (l) => l.marke},
    {key: "zielort", id: "zielort", label: "Zielort", help: "SNV Weilheim für Molotow/Burner, Lahr für Flame.", type: "text", getValue: (l) => l.zielort},
    {
        key: "menge_wunsch", id: "mengen", label: "Mengen (Ist/Zusage/Wunsch)",
        help: "Summe über alle Positionen dieser Lieferung: tatsächlich geliefert / von Cosmos zugesagt / von uns gewünscht. Sortiert nach der Wunschmenge.",
        type: "number", getValue: (l) => l.menge_wunsch,
    },
    {
        key: "ampel_verteilung", id: "ampel-spalte", label: "Wochen-Ampel",
        help: "Anteil der Positionen je Ampelfarbe - gespeichert, sonst errechnet wie im Lieferplan-Kalender. Sortiert nach Schwere (Rot vor Gelb vor Rosa vor Grün).",
        type: "number",
        getValue: (l) => {
            if (!l.ampel_verteilung || !l.ampel_verteilung.gesamt) {
                return null;
            }
            return Object.entries(AMPEL_SCHWERE_REIHENFOLGE).reduce(
                (summe, [farbe, gewicht]) => summe + (l.ampel_verteilung[farbe] || 0) * gewicht,
                0
            );
        },
    },
    {key: "zusage_quote", id: "quote-spalte", label: "Zusage-Quote", help: "Summe Zusage geteilt durch Summe Wunsch über alle Positionen dieser Lieferung.", type: "number", getValue: (l) => l.zusage_quote},
    {
        key: "versand_wunsch", id: "termine", label: "Termine (Wunsch/Geplant/Ist/Rechnung)",
        help: "Vier Termine dieser Lieferung: von uns gewünscht, von Cosmos geplant, tatsächlicher Versand, Rechnungsdatum. Sortiert nach Versand Wunsch.",
        type: "text", getValue: (l) => l.versand_wunsch,
    },
    {key: "positionen_anzahl", id: "positionen-spalte", label: "Positionen", help: "Anzahl der Farbtöne (Positionen) in dieser Lieferung.", type: "number", getValue: (l) => l.positionen_anzahl},
    {key: "letzte_aenderung", id: "letzte-aenderung", label: "Letzte Änderung", help: "Zeitpunkt der jüngsten protokollierten Änderung an Kopf oder Positionen dieser Lieferung.", type: "text", getValue: (l) => l.letzte_aenderung},
];

const AMPEL_REIHENFOLGE = ["rot", "gelb", "rosa", "gruen", "weiss"];

const STATUS_LABEL = {
    geplant: "Geplant",
    bestaetigt: "Bestätigt",
    kommissionierung: "Kommissionierung",
    teilgeliefert: "Teilgeliefert",
    geliefert: "Geliefert",
    verschoben: "Verschoben",
};

const STATUS_OPTIONEN = [
    {value: "", label: "Alle Status"},
    {value: "geplant", label: "Geplant"},
    {value: "bestaetigt", label: "Bestätigt"},
    {value: "kommissionierung", label: "Kommissionierung"},
    {value: "teilgeliefert", label: "Teilgeliefert"},
    {value: "geliefert", label: "Geliefert"},
    {value: "verschoben", label: "Verschoben"},
];

// Rückmeldung 22.09.2026 (Nachtrag zu S19): die Marken heißen MOLOTOW,
// BURNER und FLAME - nur das Label ändert sich, "value" bleibt exakt der
// gespeicherte Wert (cosmos_lieferungen.marke), sonst filtert die Auswahl
// nicht mehr.
const MARKE_OPTIONEN = [
    {value: "", label: "Alle Marken"},
    {value: "Molotow", label: "MOLOTOW"},
    {value: "Burner", label: "BURNER"},
    {value: "Flame", label: "FLAME"},
    {value: "Cosmos", label: "Cosmos"},
];

// Punkt 8 (Meeting 22.09.2026): "Cosmos-Pufferlager" als eigener, filterbarer
// Zielort neben den bestehenden zwei Werten (LieferungDetail.jsx ZIELORT_OPTIONEN).
const ZIELORT_OPTIONEN = [
    {value: "", label: "Alle Zielorte"},
    {value: "SNV Weilheim", label: "SNV Weilheim"},
    {value: "Lahr", label: "Lahr"},
    {value: "Cosmos-Pufferlager", label: "Cosmos-Pufferlager"},
];

const STATUS_BADGE = {
    geplant: "secondary",
    bestaetigt: "info",
    kommissionierung: "primary",
    teilgeliefert: "warning",
    geliefert: "success",
    verschoben: "danger",
};

function Mengenbalken({wunsch, zusage, ist}) {
    const basis = Math.max(wunsch, zusage, ist, 1);
    return (
        // #9/N5 (Sichtprüfung 2, 22.09.2026): schmaler, damit die Liste in 1.104 px passt.
        <div style={{minWidth: 100}}>
            <div className="small text-muted">
                {formatMenge(ist)}/{formatMenge(zusage)}/{formatMenge(wunsch)}
            </div>
            <div className="progress" style={{height: 6}}>
                <div className="progress-bar bg-success" style={{width: `${Math.min(100, (ist / basis) * 100)}%`}} />
                <div className="progress-bar bg-warning" style={{width: `${Math.min(100, ((zusage - ist > 0 ? zusage - ist : 0) / basis) * 100)}%`}} />
            </div>
        </div>
    );
}

/**
 * Spaltenkopf mit Erklär-Tooltip (KONVENTIONEN.md Abschnitt 4/5: "Erklär-
 * Tooltip an jedem Element") UND Sortier-Klick samt Pfeil-Indikator
 * (T14, SortableTh-Muster) - eigene Fassung statt Components/SortableTh.jsx,
 * weil hier zusätzlich der Tooltip an jedem Spaltenkopf sitzen muss.
 */
function SpaltenKopf({id, help, children, sortKey, aktiverSortKey, richtung, onSort}) {
    const istAktiv = sortKey && aktiverSortKey === sortKey;
    return (
        <th role={sortKey ? "button" : undefined} style={sortKey ? {cursor: "pointer", whiteSpace: "nowrap"} : undefined} onClick={sortKey ? () => onSort(sortKey) : undefined}>
            <OverlayTrigger placement="top" overlay={<Tooltip id={`${id}-th-tip`}>{help}{sortKey ? " Klick sortiert danach." : ""}</Tooltip>}>
                <span style={{cursor: "help", borderBottom: "1px dotted #999"}}>{children}</span>
            </OverlayTrigger>
            {sortKey && <span className="ms-1 text-muted">{istAktiv ? (richtung === "asc" ? "▲" : "▼") : ""}</span>}
        </th>
    );
}

/**
 * Wochen-Ampel einer Lieferung als schmaler gestapelter Balken (AP7 C,
 * PLAN.md Abschnitt 5 "Lieferungen"): Anteil der Positionen je Ampelfarbe
 * (gespeichert, sonst Rückfall-Ampel wie im Lieferplan-Kalender). Ohne
 * Positionen verschwindet der Balken vollständig statt eines leeren Felds.
 */
function WochenAmpelBalken({lieferungId, verteilung}) {
    if (!verteilung || !verteilung.gesamt) {
        return <span className="text-muted small">–</span>;
    }

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={`ampel-verteilung-${lieferungId}`}>
                    {AMPEL_REIHENFOLGE.filter((farbe) => verteilung[farbe] > 0)
                        .map((farbe) => `${AMPEL_LABEL[farbe]}: ${verteilung[farbe]}`)
                        .join(" · ")}
                </Tooltip>
            }
        >
            <div className="d-flex" style={{width: 90, height: 10, border: "1px solid rgba(0,0,0,.15)", borderRadius: 2, overflow: "hidden"}} tabIndex={0}>
                {AMPEL_REIHENFOLGE.map((farbe) =>
                    verteilung[farbe] > 0 ? (
                        <div key={farbe} style={{width: `${(verteilung[farbe] / verteilung.gesamt) * 100}%`, backgroundColor: AMPEL_FARBEN[farbe]}} />
                    ) : null
                )}
            </div>
        </OverlayTrigger>
    );
}

/** Zusage-Quote (Summe Zusage / Summe Wunsch) einer Lieferung, gefärbt wie eine kleine Ampel. */
function ZusageQuote({quote}) {
    if (quote === null || quote === undefined) {
        return <span className="text-muted small">–</span>;
    }
    const prozent = Math.round(quote * 1000) / 10;
    const farbklasse = prozent >= 100 ? "text-success" : prozent >= 50 ? "text-warning" : "text-danger";
    // Deutsches Komma statt Dezimalpunkt (Sichtprüfung 1, Befund 24).
    return (
        <span className={`fw-semibold ${farbklasse}`}>
            {prozent.toFixed(1).replace(".", ",")}%
        </span>
    );
}

function formatDatum(iso) {
    if (!iso) {
        return "–";
    }
    return new Date(iso).toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "2-digit"});
}

/**
 * "TT.MM.JJJJ HH:MM" ohne das Komma, das "toLocaleString('de-DE', …)"
 * zwischen Datum und Uhrzeit einfügt (Sichtprüfung 1, Befund 24).
 */
function formatZeitpunkt(iso) {
    if (!iso) {
        return "–";
    }
    const d = new Date(iso);
    const zweistellig = (n) => String(n).padStart(2, "0");
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()} ${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
}

/**
 * Liste der Cosmos-Lieferungen (AP5, PLAN.md Abschnitt 5): eine Zeile je
 * LKW mit KW, Status, Marke, Zielort, den drei Mengen und den vier
 * Terminen. Filterleiste ohne native Bedienelemente
 * (KONVENTIONEN.md Abschnitt 4). "Neue Lieferung" nur mit "cosmos.edit".
 */
export default function CosmosLieferungen() {
    const benutzerId = usePage().props.auth?.id;
    // T12 (Rückmeldung 22.09.2026): Filter UND Sortierung gemeinsam gemerkt
    // (STANDARD_FILTER oben) - sortKey bleibt "ohne Klick = natürliche
    // Server-Reihenfolge" (LieferungenQuery::liste(), T14).
    const {werte: filter, setWert: setFilterWert, setWerte: setFilterWerte, zuruecksetzen: filterZuruecksetzen, weichtAb: filterWeichtAb} = useGemerkteFilter(
        "lieferungen",
        STANDARD_FILTER,
        {benutzerId}
    );
    const [daten, setDaten] = useState(null);
    const [ladefehler, setLadefehler] = useState(null);
    const [importModalOffen, setImportModalOffen] = useState(false);
    const [importeNeuLaden, setImporteNeuLaden] = useState(0);
    const {sortKey, sortDir} = filter;

    const sortAendern = (key) => {
        if (key === sortKey) {
            setFilterWert("sortDir", sortDir === "asc" ? "desc" : "asc");
        } else {
            setFilterWerte({sortKey: key, sortDir: "asc"});
        }
    };

    const ladeListe = useCallback(() => {
        const controller = new AbortController();
        axios
            .get("/cosmos/daten/lieferungen", {
                params: {
                    kw_von: filter.kwVon || undefined,
                    kw_bis: filter.kwBis || undefined,
                    status: filter.status || undefined,
                    marke: filter.marke || undefined,
                    zielort: filter.zielort || undefined,
                    q: filter.q || undefined,
                },
                signal: controller.signal,
            })
            .then(({data}) => setDaten(data))
            .catch((e) => {
                if (!axios.isCancel(e)) {
                    setLadefehler(e.response?.data?.message || "Die Lieferungen konnten nicht geladen werden.");
                }
            });
        return () => controller.abort();
    }, [filter]);

    useEffect(() => {
        const zeitgeber = setTimeout(ladeListe, filter.q ? 300 : 0);
        return () => clearTimeout(zeitgeber);
    }, [ladeListe, filter.q]);

    const canEdit = !!daten?.meta?.abilities?.canEdit;
    const canImport = !!daten?.meta?.abilities?.canImport;
    const lieferungen = daten?.lieferungen || [];

    const lieferungenSortiert = useMemo(() => {
        if (!sortKey) {
            return lieferungen;
        }
        const spalte = SPALTEN.find((s) => s.key === sortKey);
        if (!spalte) {
            return lieferungen;
        }
        const faktor = sortDir === "asc" ? 1 : -1;
        return [...lieferungen].sort((a, b) => faktor * compareForSort(spalte.getValue(a), spalte.getValue(b), spalte.type));
    }, [lieferungen, sortKey, sortDir]);

    // Befund N2 (Rückmeldung 22.09.2026): bei 1440 px wirkte die letzte
    // Spalte "Positionen" abgeschnitten statt scrollbar (S21) - der
    // Schatten macht das waagerechte Scrollen erkennbar.
    const tabellenRef = useRef(null);
    const ueberlauf = useTabellenUeberlauf(tabellenRef, [lieferungen.length]);

    const nachImportAktualisieren = () => {
        ladeListe();
        setImporteNeuLaden((n) => n + 1);
    };

    // Sichtprüfung 1, Befund 5: "Neue Lieferung" legt nichts mehr sofort in
    // der Datenbank an (abgebrochene Anlagen blieben sonst als Leichen
    // liegen) - sie öffnet nur noch den Entwurf unter "/cosmos/lieferungen/neu"
    // (LieferungDetail.jsx). Angelegt wird erst beim ersten "Speichern" dort.
    const neueLieferungAnlegen = () => {
        router.visit("/cosmos/lieferungen/neu");
    };

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="lieferungen">
                Listet jede Cosmos-Lieferung (LKW) mit ihren vier Terminen, Status, Marke und Mengen. Hier
                werden Lieferungen und ihre Positionen auch erfasst und gepflegt - der Normalbetrieb, solange
                der Zulauf von Hand aus Mails und Lieferscheinen kommt.
            </PurposeBox>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Alle Cosmos-Lieferungen (LKW) mit Terminen, Status und Mengen; hier werden sie auch erfasst.">
                        Lieferungen
                    </HeaderWithHelp>
                </h1>
                <div className="d-flex gap-2">
                    {canImport && (
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip id="datei-import-tip">
                                    Ampel-Datei oder Oskars Bestellrechner hochladen. Die Datei wird erst geprüft -
                                    nichts wird ohne Bestätigung geändert, jeder Import ist danach rückgängig zu
                                    machen.
                                </Tooltip>
                            }
                        >
                            <span>
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setImportModalOffen(true)}>
                                    <PiUploadSimple className="me-1" />
                                    Datei importieren
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                    {canEdit && (
                        <OverlayTrigger placement="left" overlay={<Tooltip id="neue-lieferung-tip">Öffnet einen leeren Entwurf zur Erfassung - angelegt wird die Lieferung erst beim ersten Speichern.</Tooltip>}>
                            <span>
                                <button type="button" className="btn btn-primary btn-sm" onClick={neueLieferungAnlegen}>
                                    <PiPlus className="me-1" />
                                    Neue Lieferung
                                </button>
                            </span>
                        </OverlayTrigger>
                    )}
                </div>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-end mb-3 bg-body-secondary p-2 rounded">
                <div>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="kwvon-tip">Zeigt nur Lieferungen ab dieser Kalenderwoche.</Tooltip>}>
                        <label className="small fw-semibold d-block mb-1">KW von</label>
                    </OverlayTrigger>
                    <KwSelect value={filter.kwVon} onChange={(v) => setFilterWert("kwVon", v)} ariaLabel="Kalenderwoche von" />
                </div>
                <div>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="kwbis-tip">Zeigt nur Lieferungen bis zu dieser Kalenderwoche.</Tooltip>}>
                        <label className="small fw-semibold d-block mb-1">KW bis</label>
                    </OverlayTrigger>
                    <KwSelect value={filter.kwBis} onChange={(v) => setFilterWert("kwBis", v)} ariaLabel="Kalenderwoche bis" />
                </div>
                <div>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="status-filter-tip">Nur Lieferungen mit diesem Status.</Tooltip>}>
                        <label className="small fw-semibold d-block mb-1">Status</label>
                    </OverlayTrigger>
                    <SelectList value={filter.status} options={STATUS_OPTIONEN} onChange={(v) => setFilterWert("status", v)} ariaLabel="Status" size="sm" />
                </div>
                <div>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="marke-filter-tip">Nur Lieferungen dieser Marke.</Tooltip>}>
                        <label className="small fw-semibold d-block mb-1">Marke</label>
                    </OverlayTrigger>
                    <SelectList value={filter.marke} options={MARKE_OPTIONEN} onChange={(v) => setFilterWert("marke", v)} ariaLabel="Marke" size="sm" />
                </div>
                <div>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="zielort-filter-tip">Nur Lieferungen mit diesem Zielort - "Cosmos-Pufferlager" zeigt ausschließlich Puffer-Produktionen.</Tooltip>}>
                        <label className="small fw-semibold d-block mb-1">Zielort</label>
                    </OverlayTrigger>
                    <SelectList value={filter.zielort} options={ZIELORT_OPTIONEN} onChange={(v) => setFilterWert("zielort", v)} ariaLabel="Zielort" size="sm" />
                </div>
                <div className="flex-grow-1" style={{minWidth: 220}}>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="suche-tip">Sucht in Artikelnummer, Farbnummer, Bezeichnung und Cosmos-/Weclapp-Nummer.</Tooltip>}>
                        <label htmlFor="cosmos-lieferungen-suche" className="small fw-semibold d-block mb-1">
                            Suche
                        </label>
                    </OverlayTrigger>
                    <input
                        id="cosmos-lieferungen-suche"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Artikel, Farbnummer, Cosmos-Nr. …"
                        value={filter.q}
                        onChange={(e) => setFilterWert("q", e.target.value)}
                    />
                </div>
                <div className="align-self-center">
                    <AlleFilterZuruecksetzenLink sichtbar={filterWeichtAb} onClick={filterZuruecksetzen} />
                </div>
            </div>

            {ladefehler && <div className="alert alert-danger py-2 small">{ladefehler}</div>}

            {daten?.meta?.kapitalbindung_puffer?.anzahl_lieferungen > 0 && (
                <div className="alert alert-warning-subtle border border-warning-subtle py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                    <HeaderWithHelp help="Summe über alle in dieser Auswahl angezeigten Cosmos-Pufferlager-Produktionen (Meeting 22.09.2026, Punkt 8) – Kapitalbindung, kein SNV-Zulauf.">
                        <span className="fw-semibold">Im Pufferlager gebunden</span>
                    </HeaderWithHelp>
                    <span>
                        {formatMenge(daten.meta.kapitalbindung_puffer.menge_summe)} Stück über{" "}
                        {daten.meta.kapitalbindung_puffer.anzahl_lieferungen}{" "}
                        {daten.meta.kapitalbindung_puffer.anzahl_lieferungen === 1 ? "Lieferung" : "Lieferungen"}
                    </span>
                    <span className="text-muted">{daten.meta.kapitalbindung_puffer.wert_fehlt_grund}</span>
                </div>
            )}

            {daten && lieferungen.length === 0 && (
                <div className="alert alert-light border small d-flex justify-content-between align-items-center">
                    <span>Keine Lieferungen für diese Auswahl.</span>
                    {canEdit && (
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={neueLieferungAnlegen}>
                            <PiPlus className="me-1" />
                            Neue Lieferung
                        </button>
                    )}
                </div>
            )}

            {lieferungen.length > 0 && (
                <div className="position-relative">
                    <div className="table-responsive" ref={tabellenRef}>
                        <Table hover size="sm" className="bg-white align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    {SPALTEN.map((s) => (
                                        <SpaltenKopf key={s.id} id={s.id} help={s.help} sortKey={s.key} aktiverSortKey={sortKey} richtung={sortDir} onSort={sortAendern}>
                                            {s.label}
                                        </SpaltenKopf>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {lieferungenSortiert.map((l) => (
                                    <tr key={l.id} role="button" style={{cursor: "pointer"}} onClick={() => router.visit(`/cosmos/lieferungen/${l.id}`)}>
                                        <td>{kwLabel(l.kw) || "–"}</td>
                                        <td>{l.lkw_bezeichnung || "–"}</td>
                                        <td>{l.cosmos_order_id || "–"}</td>
                                        <td>{l.weclapp_bestellnummer || "–"}</td>
                                        <td>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`status-tip-${l.id}`}>Aktueller Status der gesamten Lieferung.</Tooltip>}>
                                                <span className={`badge text-bg-${STATUS_BADGE[l.status] || "secondary"}`}>{STATUS_LABEL[l.status] || l.status}</span>
                                            </OverlayTrigger>
                                        </td>
                                        <td>{markeLabel(l.marke)}</td>
                                        <td>
                                            {l.zielort || "–"}
                                            {l.ist_puffer_produktion && <PufferProduktionAbzeichen />}
                                        </td>
                                        <td>
                                            <Mengenbalken wunsch={l.menge_wunsch} zusage={l.menge_zusage} ist={l.menge_ist} />
                                        </td>
                                        <td>
                                            <WochenAmpelBalken lieferungId={l.id} verteilung={l.ampel_verteilung} />
                                        </td>
                                        <td>
                                            <ZusageQuote quote={l.zusage_quote} />
                                        </td>
                                        <td className="small text-muted">
                                            {formatDatum(l.versand_wunsch)} / {formatDatum(l.versand_geplant)} / {formatDatum(l.versand_ist)} / {formatDatum(l.rechnungsdatum)}
                                        </td>
                                        <td>{formatMenge(l.positionen_anzahl)}</td>
                                        <td className="small text-muted">{formatZeitpunkt(l.letzte_aenderung)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <TabellenUeberlaufSchatten sichtbar={ueberlauf} />
                </div>
            )}

            {canImport && <ImporteListe neuLadenSignal={importeNeuLaden} />}

            {canImport && (
                <DateiImportModal
                    show={importModalOffen}
                    onClose={() => setImportModalOffen(false)}
                    onUebernommen={nachImportAktualisieren}
                />
            )}
        </Container>
    );
}
